import { useEffect, useMemo, useRef, useState } from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AudiotrackOutlinedIcon from "@mui/icons-material/AudiotrackOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import DeleteSweepOutlinedIcon from "@mui/icons-material/DeleteSweepOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import MovieOutlinedIcon from "@mui/icons-material/MovieOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";

import { alpha } from "@mui/material/styles";

import { useTransfers } from "../../transferContext";
import { primaryTint } from "../../surfaceTints";

const finishedStatuses = new Set([
    "completed",
    "cancelled",
    "error",
]);

function formatBytes(bytes) {
    if (!bytes) {
        return "0 MB";
    }

    const units = ["B", "KB", "MB", "GB", "TB"];
    const unitIndex = Math.min(
        Math.floor(Math.log(bytes) / Math.log(1024)),
        units.length - 1,
    );
    const value = bytes / 1024 ** unitIndex;

    return `${value >= 10 || unitIndex === 0 ? value.toFixed(0) : value.toFixed(
        1,
    )} ${units[unitIndex]}`;
}

function transferProgress(transfer) {
    if (!transfer.total) {
        return 0;
    }

    return Math.min(
        100,
        Math.round((transfer.loaded / transfer.total) * 100),
    );
}

function TransferStatusIcon({ status }) {
    if (status === "completed") {
        return (
            <CheckCircleOutlineIcon
                fontSize="small"
                sx={{ color: "primary.main" }}
            />
        );
    }

    if (status === "error") {
        return <ErrorOutlineIcon fontSize="small" color="error" />;
    }

    if (status === "cancelled") {
        return (
            <HighlightOffOutlinedIcon
                fontSize="small"
                sx={{ color: "text.disabled" }}
            />
        );
    }

    return null;
}

function TransferFileIcon({ fileType }) {
    if (fileType?.includes("video")) {
        return <MovieOutlinedIcon fontSize="small" />;
    }

    if (fileType?.includes("audio")) {
        return <AudiotrackOutlinedIcon fontSize="small" />;
    }

    if (fileType?.includes("image")) {
        return <ImageOutlinedIcon fontSize="small" />;
    }

    if (fileType === "application/pdf") {
        return <PictureAsPdfOutlinedIcon fontSize="small" />;
    }

    return <InsertDriveFileOutlinedIcon fontSize="small" />;
}

function TransferRow({
    transfer,
    onCancel,
    onRetry,
    onRemove,
}) {
    const isActive = !finishedStatuses.has(transfer.status);
    const progress = transferProgress(transfer);

    return (
        <Box
            sx={{
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.25,
                }}
            >
                <Box
                    sx={{
                        width: 38,
                        height: 38,
                        borderRadius: 1.75,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: isActive ? "primary.main" : "text.secondary",
                        backgroundColor: isActive
                            ? primaryTint
                            : "background.subtle",
                    }}
                >
                    <TransferFileIcon fileType={transfer.fileType} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                flex: 1,
                                minWidth: 0,
                                fontWeight: 800,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {transfer.name}
                        </Typography>
                        <TransferStatusIcon status={transfer.status} />
                    </Box>
                    <Typography
                        variant="caption"
                        color={
                            transfer.status === "error"
                                ? "error"
                                : "text.secondary"
                        }
                    >
                        {transfer.message ||
                            (isActive ? "Downloading" : "Download")}
                    </Typography>
                </Box>

                {isActive && transfer.cancel && (
                    <Tooltip title="Cancel download">
                        <IconButton
                            aria-label={`Cancel ${transfer.name}`}
                            size="small"
                            onClick={() => onCancel(transfer.id)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {transfer.status === "error" && transfer.retry && (
                    <Tooltip title="Retry download">
                        <IconButton
                            aria-label={`Retry ${transfer.name}`}
                            size="small"
                            color="primary"
                            onClick={() => onRetry(transfer.id)}
                        >
                            <ReplayOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {!isActive && (
                    <Tooltip title="Remove from list">
                        <IconButton
                            aria-label={`Remove ${transfer.name}`}
                            size="small"
                            onClick={() => onRemove(transfer.id)}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {isActive && (
                <Box sx={{ mt: 1.25 }}>
                    <LinearProgress
                        variant={
                            transfer.total > 0
                                ? "determinate"
                                : "indeterminate"
                        }
                        value={progress}
                        sx={{
                            height: 7,
                            borderRadius: 5,
                            backgroundColor: primaryTint,
                        }}
                    />
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            mt: 0.6,
                        }}
                    >
                        <Typography variant="caption" color="text.secondary">
                            {formatBytes(transfer.loaded)}
                            {transfer.total > 0 &&
                                ` of ${formatBytes(transfer.total)}`}
                        </Typography>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 700 }}
                        >
                            {transfer.total > 0
                                ? `${progress}%`
                                : "In progress"}
                        </Typography>
                    </Box>
                </Box>
            )}

            {isActive && transfer.warning && (
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 0.75,
                        mt: 1.25,
                        p: 1,
                        borderRadius: 1.75,
                        color: (theme) =>
                            theme.palette.mode === "dark"
                                ? "warning.light"
                                : "warning.dark",
                        backgroundColor: (theme) =>
                            alpha(theme.palette.warning.main, 0.12),
                    }}
                >
                    <WarningAmberOutlinedIcon
                        fontSize="small"
                        sx={{ mt: 0.05, flexShrink: 0 }}
                    />
                    <Typography variant="caption">
                        {transfer.warning}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}

export default function TransferMenu() {
    const {
        transfers,
        panelRequest,
        cancelTransfer,
        retryTransfer,
        removeTransfer,
        clearFinishedTransfers,
    } = useTransfers();
    const [open, setOpen] = useState(false);
    const previousPanelRequest = useRef(panelRequest);

    useEffect(() => {
        if (panelRequest > previousPanelRequest.current) {
            setOpen(true);
        }

        previousPanelRequest.current = panelRequest;
    }, [panelRequest]);

    const activeTransfers = useMemo(
        () =>
            transfers.filter(
                (transfer) => !finishedStatuses.has(transfer.status),
            ),
        [transfers],
    );
    const finishedTransfers = useMemo(
        () =>
            transfers.filter((transfer) =>
                finishedStatuses.has(transfer.status),
            ),
        [transfers],
    );
    const knownActiveTransfers = activeTransfers.filter(
        (transfer) => transfer.total > 0,
    );
    const totalBytes = knownActiveTransfers.reduce(
        (sum, transfer) => sum + transfer.total,
        0,
    );
    const loadedBytes = knownActiveTransfers.reduce(
        (sum, transfer) => sum + transfer.loaded,
        0,
    );
    const overallProgress =
        totalBytes > 0
            ? Math.min(100, Math.round((loadedBytes / totalBytes) * 100))
            : 0;
    const latestTransfer = transfers[0];
    const hasUnknownProgress = activeTransfers.some(
        (transfer) => !transfer.total,
    );

    let summary = "Downloads";

    if (activeTransfers.length === 1) {
        summary =
            activeTransfers[0].total > 0
                ? `Downloading ${transferProgress(activeTransfers[0])}%`
                : "Downloading";
    } else if (activeTransfers.length > 1) {
        summary = `${activeTransfers.length} downloads`;
    } else if (latestTransfer?.status === "completed") {
        summary = "Download complete";
    } else if (latestTransfer?.status === "error") {
        summary = "Download failed";
    }

    if (transfers.length === 0) {
        return null;
    }

    return (
        <>
            <Tooltip title="Open downloads">
                <ButtonBase
                    aria-label={summary}
                    onClick={() => setOpen(true)}
                    sx={{
                        minHeight: 40,
                        px: { xs: 0.75, sm: 1.15 },
                        borderRadius: 5,
                        color: "rgba(255, 255, 255, 0.96)",
                        backgroundColor:
                            activeTransfers.length > 0
                                ? "rgba(255, 255, 255, 0.13)"
                                : "transparent",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.18)",
                        },
                    }}
                >
                    <Badge
                        badgeContent={
                            activeTransfers.length > 1
                                ? activeTransfers.length
                                : 0
                        }
                        color="secondary"
                    >
                        <Box
                            sx={{
                                position: "relative",
                                width: 32,
                                height: 32,
                                display: "grid",
                                placeItems: "center",
                            }}
                        >
                            {activeTransfers.length > 0 && (
                                <CircularProgress
                                    variant={
                                        hasUnknownProgress
                                            ? "indeterminate"
                                            : "determinate"
                                    }
                                    value={overallProgress}
                                    size={32}
                                    thickness={3}
                                    sx={{
                                        position: "absolute",
                                        color: "rgba(255, 255, 255, 0.9)",
                                    }}
                                />
                            )}
                            {activeTransfers.length > 0 ? (
                                <CloudDownloadOutlinedIcon fontSize="small" />
                            ) : latestTransfer?.status === "completed" ? (
                                <CheckCircleOutlineIcon fontSize="small" />
                            ) : latestTransfer?.status === "error" ? (
                                <ErrorOutlineIcon fontSize="small" />
                            ) : (
                                <CloudDownloadOutlinedIcon fontSize="small" />
                            )}
                        </Box>
                    </Badge>
                    <Typography
                        variant="body2"
                        sx={{
                            display: { xs: "none", md: "block" },
                            ml: 0.75,
                            mr: 0.25,
                            maxWidth: 150,
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {summary}
                    </Typography>
                </ButtonBase>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
                aria-labelledby="downloads-dialog-title"
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 3,
                            overflow: "hidden",
                        },
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                        px: { xs: 2, sm: 3 },
                        py: 2.5,
                    }}
                >
                    <Box
                        sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            flexShrink: 0,
                            color: "primary.main",
                            backgroundColor: "action.hover",
                        }}
                    >
                        <CloudDownloadOutlinedIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                            id="downloads-dialog-title"
                            variant="h5"
                            component="h2"
                            sx={{ lineHeight: 1.1 }}
                        >
                            Downloads
                        </Typography>
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5 }}
                        >
                            Current downloads and recent activity.
                        </Typography>
                    </Box>
                    <IconButton
                        aria-label="Close downloads"
                        onClick={() => setOpen(false)}
                        sx={{ mt: -0.75, mr: -1 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {transfers.length === 0 ? (
                        <Box
                            sx={{
                                minHeight: 220,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                textAlign: "center",
                                color: "text.secondary",
                            }}
                        >
                            <CloudDownloadOutlinedIcon
                                sx={{ fontSize: 44, mb: 1, opacity: 0.4 }}
                            />
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    color: "text.primary",
                                    fontWeight: 800,
                                }}
                            >
                                No downloads yet
                            </Typography>
                            <Typography variant="body2">
                                Active and completed downloads will appear
                                here.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {activeTransfers.length > 0 && (
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{
                                            display: "block",
                                            mb: 0.75,
                                            fontWeight: 800,
                                        }}
                                    >
                                        In progress
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {activeTransfers.map((transfer) => (
                                            <TransferRow
                                                key={transfer.id}
                                                transfer={transfer}
                                                onCancel={cancelTransfer}
                                                onRetry={retryTransfer}
                                                onRemove={removeTransfer}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}

                            {finishedTransfers.length > 0 && (
                                <Box>
                                    <Typography
                                        variant="overline"
                                        color="text.secondary"
                                        sx={{
                                            display: "block",
                                            mb: 0.75,
                                            fontWeight: 800,
                                        }}
                                    >
                                        Recent
                                    </Typography>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "column",
                                            gap: 1,
                                        }}
                                    >
                                        {finishedTransfers.map((transfer) => (
                                            <TransferRow
                                                key={transfer.id}
                                                transfer={transfer}
                                                onCancel={cancelTransfer}
                                                onRetry={retryTransfer}
                                                onRemove={removeTransfer}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>

                <DialogActions
                    sx={{
                        px: { xs: 2, sm: 3 },
                        py: 1.5,
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        color="inherit"
                        startIcon={<DeleteSweepOutlinedIcon />}
                        onClick={clearFinishedTransfers}
                        disabled={finishedTransfers.length === 0}
                    >
                        Clear finished
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        onClick={() => setOpen(false)}
                    >
                        Done
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
